import { inject, provide, ref, computed, watch } from 'vue'
import type {
  FormState,
  ValidationRule,
  FieldValidation,
  FormValidation,
  UseFormOptions,
  UseFormReturn
} from '../types/form'

const FormStateKey = Symbol('FormState')

export function createFormState(initialValues: Record<string, unknown> = {}): FormState {
  const values = ref(initialValues)
  const touched = ref<Record<string, boolean>>({})
  const validations = ref<Record<string, FieldValidation>>({})
  const isSubmitting = ref(false)

  const errors = computed(() => {
    const errorMap: Record<string, string[]> = {}
    Object.entries(validations.value).forEach(([field, validation]) => {
      if (validation.errors.length > 0) {
        errorMap[field] = validation.errors
      }
    })
    return errorMap
  })

  const setFieldValue = (field: string, value: unknown) => {
    values.value[field] = value
    validateField(field)
  }

  const setFieldTouched = (field: string, isTouched = true) => {
    touched.value[field] = isTouched
    if (isTouched) {
      validateField(field)
    }
  }

  const addValidation = (field: string, rules: ValidationRule[]) => {
    validations.value[field] = {
      value: values.value[field],
      rules,
      errors: []
    }
  }

  const validateField = async (field: string): Promise<boolean> => {
    const fieldValidation = validations.value[field]
    if (!fieldValidation) return true

    const fieldValue = values.value[field]
    fieldValidation.errors = []

    for (const rule of fieldValidation.rules) {
      const isValid = await rule.validator(fieldValue)
      if (!isValid) {
        fieldValidation.errors.push(rule.message)
      }
    }

    return fieldValidation.errors.length === 0
  }

  const validateForm = async (): Promise<FormValidation> => {
    const validationResults = await Promise.all(
      Object.keys(validations.value).map(field => validateField(field))
    )

    const isValid = validationResults.every(result => result)
    return {
      isValid,
      errors: errors.value
    }
  }

  return {
    values: computed(() => values.value),
    touched: computed(() => touched.value),
    validations: computed(() => validations.value),
    errors,
    isSubmitting: computed(() => isSubmitting.value),
    setFieldValue,
    setFieldTouched,
    validateField,
    addValidation,
    validateForm
  }
}

export function useForm<T extends Record<string, unknown>>(
  initialValues: T,
  options: UseFormOptions = {
    validateOnMount: false,
    validateOnBlur: true,
    validateOnInput: false,
    validateOnChange: true
  }
): UseFormReturn<T> {
  const values = ref<T>({ ...initialValues })
  const touched = ref<Record<string, boolean>>({})
  const errors = ref<Record<string, string[]>>({})
  const isSubmitting = ref(false)
  const isDirty = ref(false)
  const validations = ref<Record<string, ValidationRule[]>>({})

  const isValid = computed(() => Object.keys(errors.value).length === 0)

  const validateField = async (field: keyof T): Promise<boolean> => {
    const fieldRules = validations.value[field as string]
    if (!fieldRules) return true

    const fieldValue = values.value[field]
    const fieldErrors: string[] = []

    for (const rule of fieldRules) {
      const isValid = await rule.validator(fieldValue)
      if (!isValid) {
        fieldErrors.push(rule.message)
      }
    }

    if (fieldErrors.length > 0) {
      errors.value[field as string] = fieldErrors
      return false
    } else {
      delete errors.value[field as string]
      return true
    }
  }

  const validate = async (): Promise<boolean> => {
    const results = await Promise.all(
      Object.keys(validations.value).map(field => validateField(field as keyof T))
    )
    return results.every(result => result)
  }

  const handleSubmit = (onSubmit: (values: T) => Promise<void>) => async (e?: Event) => {
    if (e) {
      e.preventDefault()
    }

    isSubmitting.value = true
    try {
      const isValid = await validate()
      if (isValid) {
        await onSubmit(values.value)
      }
    } finally {
      isSubmitting.value = false
    }
  }

  const resetForm = () => {
    values.value = { ...initialValues }
    touched.value = {}
    errors.value = {}
    isDirty.value = false
  }

  const setFieldValue = <K extends keyof T>(field: K, value: T[K]) => {
    values.value[field] = value
    isDirty.value = true
    if (options.validateOnInput) {
      validateField(field)
    }
  }

  const setFieldTouched = (field: keyof T, isTouched = true) => {
    touched.value[field as string] = isTouched
    if (isTouched && options.validateOnBlur) {
      validateField(field)
    }
  }

  const addValidation = (field: keyof T, rules: ValidationRule[]) => {
    validations.value[field as string] = rules
    if (options.validateOnMount) {
      validateField(field)
    }
  }

  // Watch for value changes
  watch(values, () => {
    if (options.validateOnChange) {
      validate()
    }
  }, { deep: true })

  return {
    values: computed(() => values.value),
    touched: computed(() => touched.value),
    errors: computed(() => errors.value),
    isValid,
    isDirty: computed(() => isDirty.value),
    isSubmitting: computed(() => isSubmitting.value),
    validate,
    handleSubmit,
    resetForm,
    setFieldValue,
    setFieldTouched,
    addValidation
  }
}

export function provideFormState(formState: FormState) {
  provide(FormStateKey, formState)
}

export function useFormContext() {
  const formState = inject<FormState | null>(FormStateKey, null)
  return { formState }
}

// Common validation rules
export const createValidationRule = (
  validator: (value: unknown) => boolean | Promise<boolean>,
  message: string
): ValidationRule => ({
  validator,
  message
})

export const required = (message = 'This field is required'): ValidationRule =>
  createValidationRule(value => value != null && String(value).length > 0, message)

export const email = (message = 'Invalid email address'): ValidationRule =>
  createValidationRule(
    value => typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    message
  )

export const minLength = (length: number, message = `Minimum length is ${length}`): ValidationRule =>
  createValidationRule(
    value => typeof value === 'string' && value.length >= length,
    message
  )

export const maxLength = (length: number, message = `Maximum length is ${length}`): ValidationRule =>
  createValidationRule(
    value => typeof value === 'string' && value.length <= length,
    message
  )

export const pattern = (regex: RegExp, message = 'Invalid format'): ValidationRule =>
  createValidationRule(
    value => typeof value === 'string' && regex.test(value),
    message
  )

export const custom = (
  validator: (value: unknown) => boolean | Promise<boolean>,
  message = 'Invalid value'
): ValidationRule => createValidationRule(validator, message)
